import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment/moment";
import { App, Button, List, Progress, Segmented } from "antd";

import { ExclamationCircleFilled } from "@ant-design/icons";
import { DeleteOutlined } from "@ant-design/icons";
import ProfileAvatar from "components/UI/ProfileAvatar";

import { apiDeleteModel } from "api/apiAiModels";
import GenericCard from "components/UI/GenericCard/GenericCard";
import SavePredictionsButton from "./SavePredictionsButton";
import DashedTooltip from "components/UI/DashedTooltip";
import VirtualList from "rc-virtual-list";

const SavePredictionsChooseModel = (props) => {
  const { modelsList, getModelsList, handleCheckAiSymbol } = props;
  const { message, modal } = App.useApp();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSearchAllPending, setIsSearchAllPending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [modelName, setModelName] = useState("");
  const [resolution, setResolution] = useState("1d");

  const deleteModelById = async (modelId) => {
    try {
      await apiDeleteModel(modelId);
      getModelsList(resolution);
      message.success("Model deleted successfully");
    } catch (err) {
      message.error(err.response.data.message);
    }
  };

  const showDeleteConfirm = (modelId) => {
    modal.confirm({
      title: "Are you sure you want to delete this model?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteModelById(modelId);
      },
      onCancel() {},
    });
  };

  const onSearchAllSuccess = () => {
    setIsSearchAllPending(false);
    setModelName("");
  };

  const onSearchAllFailure = (message = "Error") => {
    setIsSearchAllPending(false);
    setErrorMessage(message);
  };

  const onSearchAllProgressUpdate = (progressPercent, name) => {
    setProgress(progressPercent);
    if (name) {
      setModelName(name);
    }
  };

  const handleSearchAllModels = () => {
    setIsSearchAllPending(true);
    setProgress(0);
    setErrorMessage("");

    handleCheckAiSymbol(
      null,
      onSearchAllSuccess,
      onSearchAllFailure,
      onSearchAllProgressUpdate
    );
  };

  useEffect(() => {
    getModelsList(resolution);
  }, [resolution]);

  return (
    <GenericCard title="Choose models">
      <div>
        <p className="mb-3">
          <span className="pe-2">Resolution:</span>
          <Segmented
            value={resolution}
            onChange={(value) => setResolution(value)}
            options={[
              { label: "1D", value: "1d" },
              { label: "1H", value: "1h" },
            ]}
          />
        </p>
        <div className="results-table-buttons">
          <Button disabled={isSearchAllPending} onClick={handleSearchAllModels}>
            Search all models ({modelsList.length})
          </Button>
        </div>

        {progress !== null ? (
          <div>
            <div
              style={{
                width: 170,
              }}
            >
              <Progress
                percent={progress}
                status={`${isSearchAllPending ? "active" : ""}${
                  errorMessage ? "exception" : ""
                }`}
              />
            </div>
            {modelName && (
              <p
                style={{ color: "#ffffff" }}
                className="pb-2"
              >{`Predicting: ${modelName}`}</p>
            )}
          </div>
        ) : (
          <></>
        )}
        <List bordered size="small">
          <VirtualList
            data={modelsList}
            itemHeight={160}
            height={480}
            itemKey="_id"
          >
            {(item) => (
              <List.Item
                actions={[
                  item.usedByStrategies === 0 && (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => showDeleteConfirm(item._id)}
                      danger
                      ghost
                    />
                  ),
                  <SavePredictionsButton
                    model={item}
                    handleCheckAiSymbol={handleCheckAiSymbol}
                    isDisabled={isSearchAllPending}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={<ProfileAvatar user={item?.creator} />}
                  title={item.name}
                  description={
                    <>
                      <div className="flex">
                        <div className="pe-2">
                          <DashedTooltip
                            text={item?.results?.stocks?.map((item, index) =>
                              index === 0 ? item : `, ${item}`
                            )}
                          >
                            <span className="">Trained data:</span>
                          </DashedTooltip>
                        </div>
                        <div
                          className="flex gap-2"
                          style={{ color: "#ffffff" }}
                        >
                          <div>
                            {moment(item.results?.start_date).format(
                              "DD/MM/YY"
                            )}
                          </div>
                          {" - "}
                          <div>
                            {moment(item.results?.end_date).format("DD/MM/YY")}
                          </div>
                        </div>
                      </div>
                      <p>
                        Profit target:{" "}
                        <span style={{ color: "#ffffff" }}>
                          {((item.results?.perc_to_gain - 1) * 100).toFixed(2)}%
                          in {item.results?.days} bars
                        </span>
                      </p>
                      <p>
                        <span>Buy/total: </span>
                        <span style={{ color: "#ffffff" }} className="pe-4">
                          {(item.results?.buy_from_total * 100).toFixed(1)}%
                        </span>
                        <span>Accuarcy: </span>
                        <span style={{ color: "#ffffff" }} className="pe-4">
                          {(item.results?.accuracy * 100).toFixed(1)}%
                        </span>
                        <span>Type: </span>
                        <span style={{ color: "#ffffff" }} className="pe-4">
                          {item.modelType?.toUpperCase() || "N/A"}
                        </span>
                        <span>Resolution: </span>
                        <span style={{ color: "#ffffff" }} className="pe-4">
                          {item.resolution?.toUpperCase() || "N/A"}
                        </span>
                      </p>
                      <p>
                        Indicators:{" "}
                        {item.results?.indicators &&
                          Object.entries(item.results.indicators).map(
                            ([indicator, value]) => {
                              if (typeof value === "object" && value !== null) {
                                return (
                                  <span
                                    style={{ color: "#ffffff" }}
                                    className="pe-4 inline-flex"
                                  >
                                    {indicator}:{" "}
                                    {Object.values(value).map(
                                      (value) => `${value} `
                                    )}
                                  </span>
                                );
                              }
                              return (
                                <span
                                  style={{ color: "#ffffff" }}
                                  className="pe-4 inline-flex"
                                >
                                  {indicator}: {value}
                                </span>
                              );
                            }
                          )}
                      </p>
                      <p>
                        {item?.creator
                          ? `Created by ${item.creator.firstName} ${item?.creator?.lastName}, `
                          : ""}
                        {moment(item.date).format("HH:mm DD/MM/YY")}
                        {item.usedByStrategies > 0 && (
                          <span style={{ color: "#ffffff" }} className="ps-4">
                            Used by {item.usedByStrategies} strategies
                          </span>
                        )}
                      </p>
                    </>
                  }
                />
              </List.Item>
            )}
          </VirtualList>
        </List>
      </div>
    </GenericCard>
  );
};

SavePredictionsChooseModel.propTypes = {
  setChoosenModel: PropTypes.func,
  choosenModel: PropTypes.object,
};

export default SavePredictionsChooseModel;
